import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const STATUS_STEPS = [
  { key: 'pending', icon: '📋', label: 'Order Placed', desc: 'Your order has been received' },
  { key: 'packed', icon: '📦', label: 'Packed', desc: 'Your order is being packed' },
  { key: 'dispatched', icon: '🚚', label: 'Out for Delivery', desc: 'On the way to your apartment' },
  { key: 'delivered', icon: '✅', label: 'Delivered', desc: 'Order delivered successfully!' },
];

const getStepIndex = (status) => STATUS_STEPS.findIndex(s => s.key === status);

const OrderTrackCard = ({ order }) => {
  const stepIdx = getStepIndex(order.order_status);

  return (
    <div className="order-track-card">
      <div className="track-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order ID</div>
          <div style={{ fontSize: 18, fontWeight: 900, fontFamily: 'monospace', marginTop: 2 }}>#{order.id.slice(0, 8).toUpperCase()}</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>📞 {order.customer_name} · {order.phone}</div>
          {order.apartment_name && <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 2 }}>🏢 {order.apartment_name}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--green-dark)' }}>₹{parseFloat(order.total_amount).toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
          {order.order_status === 'cancelled' && (
            <div style={{ marginTop: 8, padding: '4px 12px', background: '#fee2e2', color: '#b91c1c', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>CANCELLED</div>
          )}
        </div>
      </div>

      {order.order_status !== 'cancelled' && (
        <div className="track-status-steps">
          {STATUS_STEPS.map((step, i) => {
            const done = i <= stepIdx;
            const current = i === stepIdx;
            return (
              <div key={step.key} className={`track-step ${done ? 'done' : ''}`}>
                <div className={`step-dot ${done ? 'done' : current ? 'current' : ''}`}>
                  {done ? '✓' : step.icon}
                </div>
                <div className="step-info">
                  <div className="step-title" style={{ color: done ? 'var(--text)' : 'var(--text-3)' }}>{step.label}</div>
                  <div className="step-sub">{current ? '⏱ In progress...' : done ? '✅ Completed' : step.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Items */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Items</div>
        {(Array.isArray(order.items) ? order.items : []).map(item => (
          <div key={item.productId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', color: 'var(--text-2)' }}>
            <span>{item.name} × {item.quantity}</span>
            <span style={{ fontWeight: 600 }}>₹{item.subtotal?.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('orderId');
  const [input, setInput] = useState(searchParams.get('orderId') || '');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!input.trim()) { setError('Please enter an Order ID or phone number'); return; }
    setLoading(true);
    setError('');
    setOrders(null);
    try {
      const params = tab === 'orderId' ? { orderId: input.trim() } : { phone: input.trim() };
      const { data } = await api.get('/orders/track', { params });
      setOrders(Array.isArray(data.data) ? data.data : [data.data]);
    } catch (err) {
      setError(
        tab === 'phone'
          ? err.response?.data?.message || 'No active orders found for this phone number. Only pending, packed, and dispatched orders are shown.'
          : err.response?.data?.message || 'No orders found. Check your Order ID.'
      );
    } finally {
      setLoading(false); }
  };

  useEffect(() => {
    if (searchParams.get('orderId')) handleSearch();
  }, []); // eslint-disable-line

  return (
    <div className="page tracking-page">
      <h1>📍 Track Your Order</h1>
      <p>Enter your Order ID or phone number to see your delivery status in real-time.</p>

      {/* Tabs */}
      <div className="search-tabs">
        <button className={`tab-btn ${tab === 'orderId' ? 'active' : ''}`} onClick={() => setTab('orderId')}>🔑 Order ID</button>
        <button className={`tab-btn ${tab === 'phone' ? 'active' : ''}`} onClick={() => setTab('phone')}>📞 Phone Number</button>
      </div>

      <form onSubmit={handleSearch}>
        <div className="track-search">
          {tab === 'phone' && (
            <div style={{ padding: '0 16px', fontWeight: 600, color: 'var(--text-3)', background: '#f8fafc', border: '1px solid var(--border)', borderRight: 'none', borderRadius: '12px 0 0 12px', display: 'flex', alignItems: 'center' }}>+91</div>
          )}
          <input
            id="track-input"
            className="form-input"
            style={{ flex: 1, borderRadius: tab === 'phone' ? '0 12px 12px 0' : 12 }}
            placeholder={tab === 'orderId' ? 'e.g. A1B2C3D4...' : 'e.g. 9876543210'}
            value={tab === 'phone' ? input.replace('+91 ', '') : input}
            onChange={e => {
              if (tab === 'phone') {
                const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                setInput(v ? `+91 ${v}` : '');
              } else {
                setInput(e.target.value);
              }
            }}
            pattern={tab === 'phone' ? "[0-9]{10}" : undefined}
            maxLength={tab === 'phone' ? 10 : undefined}
          />
          <button id="track-btn" type="submit" className="btn btn-green" disabled={loading} style={{ borderRadius: 12, whiteSpace: 'nowrap' }}>
            {loading ? <><span className="spinner-sm"></span> Searching...</> : '🔍 Track'}
          </button>
        </div>
      </form>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12, padding: '14px 18px', color: '#b91c1c', fontSize: 14, marginBottom: 20 }}>
          ⚠️ {error}
        </div>
      )}

      {orders && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-3)', marginBottom: 14 }}>
            Found {orders.length} active order{orders.length !== 1 ? 's' : ''}
          </div>
          {orders.map(o => <OrderTrackCard key={o.id} order={o} />)}
        </div>
      )}

      {!orders && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-3)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <p style={{ fontSize: 15 }}>Enter your Order ID or phone number to track your delivery</p>
        </div>
      )}
    </div>
  );
};

export default TrackingPage;
